import { LocalizationProvider } from "@mui/x-date-pickers";
import { TimePicker as MuiTimePicker } from '@mui/x-date-pickers/TimePicker';
import { DateTime } from "luxon";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";

import { clockHourOrderSx } from "./timePickerHours";

interface TimePickerProps {
  /** Called whenever the user picks a new time. */
  onChange: (fullDate?: DateTime) => void;
  /** Called once a time is finished: the last clock step, or a completed edit in the field. */
  onAccept?: (fullDate?: DateTime) => void;
  /** Initial date to show (defaults to “now”). */
  initialDate?: DateTime | null;
  minValue: DateTime;
  maxValue: DateTime;
  clearable?: boolean;
  /** Label to show next to the control. */
  label?: string;
  disabled?: boolean;
}

export function TimePicker({onChange, onAccept, initialDate, minValue, maxValue, clearable = false, label, disabled = false}: TimePickerProps) {
  const isCrossMidnight = minValue.toISODate() !== maxValue.toISODate();

  const isTimeOutsideRange = (testTime: DateTime) => {
    const testMinutes = testTime.hour * 60 + testTime.minute;
    const minMinutes = minValue.hour * 60 + minValue.minute;
    const maxMinutes = maxValue.hour * 60 + maxValue.minute;

    if (!isCrossMidnight) {
      return testMinutes < minMinutes || testMinutes > maxMinutes;
    }

    return testMinutes > maxMinutes && testMinutes < minMinutes;
  };

  const withinRange = (newVal: unknown): DateTime | undefined => {
    if (!newVal || !DateTime.isDateTime(newVal)) return undefined;
    if (!isTimeOutsideRange(newVal)) return newVal;

    const testMinutes = newVal.hour * 60 + newVal.minute;
    const minMinutes = minValue.hour * 60 + minValue.minute;
    const maxMinutes = maxValue.hour * 60 + maxValue.minute;
    // If the selected time is outside the range, adjust it to the nearest boundary
    const minDiff = Math.abs(testMinutes - minMinutes);
    const maxDiff = Math.abs(testMinutes - maxMinutes);
    return minDiff < maxDiff ? minValue : maxValue;
  };

  const value: DateTime | null =
    initialDate === undefined ? null : initialDate;

  const clockSx = clockHourOrderSx(minValue, maxValue);

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <MuiTimePicker
        ampm={false}
        disabled={disabled}
        label={label}
        value={value}
        onChange={(newVal) => onChange(withinRange(newVal))}
        onAccept={onAccept ? (newVal) => onAccept(withinRange(newVal)) : undefined}
        shouldDisableTime={(value) => {
          if (!DateTime.isDateTime(value)) return false;
          return isTimeOutsideRange(value);
        }}
        skipDisabled
        slotProps={{
          desktopPaper: { sx: clockSx },
          mobilePaper: { sx: clockSx },
          textField: {
            fullWidth: false,
            size: "small",
            InputLabelProps: {shrink: true},
            InputProps: {
              endAdornment: (value && clearable) ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => onChange(undefined)}
                    edge="end"
                    aria-label="Clear time"
                  >
                    <ClearIcon fontSize="small" className="!text-theme-text" />
                  </IconButton>
                </InputAdornment>
              ) : null
            },
          } as any,
        }}
      />
    </LocalizationProvider>
  );
}
